import React, { useEffect, useMemo, useRef } from "react";
import { cn } from "@/libs/utils";
import {
  ArcadePartner,
  bookCta,
  dirUrl,
  FlatLocation,
  partnerColor,
} from "../_constants/where-to-try-constants";
import { useTrans } from "@/utils/useTrans";
import { analyticsInstance } from "@/libs/analytics";
import Sandbox from "@/v2-components/pages/about/svgs/sandbox";
import Hologate from "@/v2-components/pages/about/svgs/hologate";
import Vex from "@/v2-components/pages/about/svgs/vex";

// 데이터 logoComponent 문자열 → 로고 컴포넌트. 없으면 모노그램 폴백(프로토 partnerLogo).
const LOGO_REGISTRY: Record<string, React.FC> = { Sandbox, Hologate, Vex };

interface LocationListProps {
  // 파트너 섹션 헤더(로고·이름·색점·개수·설명)용. 노출 파트너 순서.
  partners: ArcadePartner[];
  locations: FlatLocation[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  // 정밀 좌표(userLoc) 있을 때 매장별 거리(km). "Use my location" 후에만 채워진다.
  distances?: Record<string, number>;
  // 모바일 시트 내부 스크롤 ref(커밋10 자동 스크롤 대상). 데스크톱은 부모 list-col 이 스크롤.
  rowRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

export default function LocationList({
  partners,
  locations,
  selectedId,
  onSelect,
  distances,
  rowRefs,
}: LocationListProps) {
  const { t } = useTrans(["where-to-try"]);
  const localRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const itemRefs = rowRefs ?? localRefs;

  // 파트너별 그룹(노출 순서). locations 는 이미 Nearest 정렬 → 그룹 내 순서 보존.
  const groups = useMemo(() => {
    const byId = new Map<string, FlatLocation[]>();
    for (const loc of locations) {
      const arr = byId.get(loc.partnerId);
      if (arr) arr.push(loc);
      else byId.set(loc.partnerId, [loc]);
    }
    return partners
      .map((p) => ({ partner: p, venues: byId.get(p.id) ?? [] }))
      .filter((g) => g.venues.length > 0);
  }, [locations, partners]);

  useEffect(() => {
    if (!selectedId) return;
    const el = itemRefs.current[selectedId];
    // 데스크톱 list-col 스크롤용. 모바일 시트 내부 스크롤은 커밋10에서 동기 scrollTop 으로 별도 처리.
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId, itemRefs]);

  if (locations.length === 0) {
    return (
      <div className="flex h-40 w-full items-center justify-center rounded-2xl bg-coolgray-10 text-sm text-coolgray-60">
        {t("No locations match your filters.")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {groups.map(({ partner, venues }) => {
        const Logo = partner.logoComponent
          ? LOGO_REGISTRY[partner.logoComponent]
          : undefined;
        return (
          <section key={partner.id} className="flex flex-col gap-2">
            {/* 파트너 섹션 헤더: 로고 타일 + 색점 + 이름 + 개수 + 설명(1회). 프로토 .phead */}
            <div className="flex items-start gap-3">
              {/* 프로토 .plogo: 64×64 gray 타일 + 내부 로고 72% 박스(object-contain).
                  로고 SVG 고정 치수는 size-full 로 박스에 맞춰 축소(비율 유지). */}
              <div className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-coolgray-10 text-coolgray-100">
                {Logo ? (
                  <span className="flex size-[72%] items-center justify-center [&>svg]:size-full [&>svg]:object-contain">
                    <Logo />
                  </span>
                ) : (
                  <span className="text-[22px] font-600 text-coolgray-70">
                    {partner.name.charAt(0)}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-600 text-coolgray-100">
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: partnerColor(partner.id) }}
                  />
                  {partner.name}
                  <span className="text-sm font-400 text-coolgray-50">
                    <span className="mr-1.5 text-coolgray-40">•</span>
                    {t("{{count}} venues", { count: venues.length })}
                  </span>
                </h2>
                {partner.description && (
                  <p className="mt-1 max-w-prose text-sm text-coolgray-60">
                    {partner.description}
                  </p>
                )}
              </div>
            </div>

            {/* venue 행 목록 — 테두리 카드 없이 행 사이 구분선(divide)만(사용자 선호: 더 깔끔). */}
            <div className="flex flex-col divide-y divide-coolgray-10">
              {venues.map((loc) => (
                <VenueRow
                  key={`${loc.partnerId}-${loc.id}`}
                  loc={loc}
                  selected={loc.id === selectedId}
                  distance={distances?.[loc.id]}
                  onSelect={onSelect}
                  refCb={(el) => {
                    itemRefs.current[loc.id] = el;
                  }}
                  t={t}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function VenueRow({
  loc,
  selected,
  distance,
  onSelect,
  refCb,
  t,
}: {
  loc: FlatLocation;
  selected: boolean;
  distance?: number;
  onSelect: (id: string | null) => void;
  refCb: (el: HTMLDivElement | null) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const cta = bookCta(loc);
  const directions = dirUrl(loc);
  const venueName = loc.venue ?? loc.city;

  return (
    <div
      ref={refCb}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={t("Show {{venue}} on map", { venue: venueName })}
      onClick={() => onSelect(selected ? null : loc.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(selected ? null : loc.id);
        }
      }}
      className={cn(
        "flex cursor-pointer items-center justify-between gap-3 border-l-2 py-3 pl-3 pr-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-60",
        // Carbon selected: blue-10 배경 + blue-60 좌측 액센트 바.
        selected
          ? "border-l-blue-60 bg-blue-10"
          : "border-l-transparent hover:bg-coolgray-10",
      )}
    >
      <div className="min-w-0">
        <h3 className="truncate text-sm font-500 text-coolgray-100">
          {venueName}
        </h3>
        <div className="text-xs text-coolgray-60">
          {[loc.city, loc.country].filter(Boolean).join(", ")}
          {distance != null && (
            <span className="text-coolgray-50">
              {" · "}
              {distance < 1
                ? t("<1 km")
                : t("{{distance}} km", { distance: Math.round(distance) })}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {loc.soon ? (
          <span className="inline-flex h-9 items-center rounded-full bg-coolgray-10 px-4 text-xs font-600 text-coolgray-40">
            {t("Opening Soon")}
          </span>
        ) : (
          cta && (
            <a
              href={cta.href}
              target={cta.ext ? "_blank" : undefined}
              rel={cta.ext ? "noopener noreferrer" : undefined}
              onClick={(e) => {
                e.stopPropagation();
                analyticsInstance?.whereToTryVenueBook({
                  venue: venueName,
                  country: loc.country,
                });
              }}
              className="inline-flex h-9 items-center gap-1 rounded-full bg-blue-60 px-4 text-xs font-600 text-white transition-colors hover:bg-blue-70"
            >
              {cta.kind === "book"
                ? t("Book a session")
                : cta.kind === "bookOn"
                  ? t("Book on {{partner}}", { partner: loc.partnerName })
                  : t("Contact")}
              {/* 외부링크 아이콘은 kind 가 아니라 ext 하나로 결정 — target/rel(위)과 같은 기준.
                  kind 별로 복제하면 새 kind 추가 시 아이콘 누락이 조용히 통과한다. */}
              {cta.ext && <ExternalIcon className="size-3" />}
            </a>
          )
        )}

        {directions && (
          <a
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              analyticsInstance?.whereToTryVenueDirections({
                venue: venueName,
                country: loc.country,
              });
            }}
            aria-label={t("Directions to {{venue}}", { venue: venueName })}
            // 프로토: Directions = blue-outline 원형 아이콘 버튼(hover 시 blue fill).
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-blue-60 text-blue-60 transition-colors hover:bg-blue-60 hover:text-white"
          >
            <DirectionsIcon className="size-4" />
          </a>
        )}
      </div>
    </div>
  );
}

function ExternalIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </svg>
  );
}

function DirectionsIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
